import { apiClient } from "./apiClient";
import type {
  H1PromoteCandidates,
  IssuesListResponse,
  IssueFilters,
  IssueSort,
  PageIssue,
  UpdateIssueRequest,
  BulkUpdateRequest,
  BulkUpdateResponse,
  ApplySingleFixResponse,
  ApplyFixesResponse,
  AuditRunSummary,
  CrawlConfig,
  CrawlStatus,
  StartCrawlResponse,
  IssueSeverity,
  LargeImageProofResult,
  TechnicalAuditFixPreview,
  ProposedFix,
  FixHistory,
  FixHistoryEntry,
  RecoveryActions,
  SourcePublishState,
  PublishTargets,
  PublishSourceResult,
  TicketsResponse,
  AllTicketsResponse,
  RecommendationBacklogResponse,
  DuplicateGroupsResponse,
  BulkGenerateSuggestionsResponse,
} from "../types/technicalAudit";

export interface AuditRunsListResponse {
  runs: AuditRunSummary[];
  total: number;
}

const normalizeSeverity = (severity?: string): IssueSeverity => {
  if (severity === "high" || severity === "critical") return "critical";
  if (severity === "medium" || severity === "warning") return "warning";
  if (severity === "low" || severity === "info") return "info";
  return "warning";
};

const normalizeSeveritySummary = (summary?: Record<string, number>) => ({
  critical: (summary?.critical ?? 0) + (summary?.high ?? 0),
  warning: (summary?.warning ?? 0) + (summary?.medium ?? 0),
  info: (summary?.info ?? 0) + (summary?.low ?? 0),
});

const normalizeStatusSummary = (summary?: Record<string, number>) => ({
  pending: summary?.pending ?? 0,
  approved: summary?.approved ?? 0,
  rejected: summary?.rejected ?? 0,
  applied: summary?.applied ?? 0,
  verified: summary?.verified ?? 0,
  failed: summary?.failed ?? 0,
  skipped: summary?.skipped ?? 0,
  protected: summary?.protected ?? 0,
  obsolete: summary?.obsolete ?? 0,
});

const emptyIssueSummary = () => ({
  total: 0,
  by_severity: normalizeSeveritySummary(),
  by_status: normalizeStatusSummary(),
  by_type: {} as IssuesListResponse["summary"]["by_type"],
});

const normalizeRunStatus = (status?: string): AuditRunSummary["status"] => {
  if (status === "completed" || status === "failed") return status;
  if (status === "pending" || status === "running" || status === "processing") {
    return status === "pending" ? "pending" : "running";
  }
  return "pending";
};

type RawApiObject = Record<string, unknown>;

const asRecord = (value: unknown): RawApiObject =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as RawApiObject)
    : {};

const maybeRecord = (value: unknown): RawApiObject | undefined =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as RawApiObject)
    : undefined;

const asString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const asNumber = (value: unknown): number | null =>
  typeof value === "number" ? value : null;

const readContentType = (raw: RawApiObject): PageIssue["content_type"] => {
  const direct = raw.content_type ?? raw.contentType;
  if (
    direct === "page" ||
    direct === "post" ||
    direct === "product" ||
    direct === "media"
  ) {
    return direct;
  }
  const details = asRecord(raw.issue_details ?? raw.issueDetails);
  const detailValue =
    details.content_type ??
    details.contentType ??
    details.wp_post_type ??
    details.post_type;
  return detailValue === "page" ||
    detailValue === "post" ||
    detailValue === "product" ||
    detailValue === "media"
    ? detailValue
    : null;
};

const parseMaybeJson = (value: unknown): ProposedFix | null => {
  if (typeof value !== "string") return value ?? null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed) as ProposedFix;
  } catch {
    return {
      recommendation: trimmed,
      auto_fixable: false,
    };
  }
};

const parseMaybeObject = (value: unknown): RawApiObject | undefined => {
  if (!value) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    try {
      const parsed = JSON.parse(trimmed);
      return maybeRecord(parsed);
    } catch {
      return undefined;
    }
  }
  return maybeRecord(value);
};

// ⚠ Runtime twin of the IssueStatus type — a status missing here is silently
// rewritten to "pending" by normalizeFixStatus below, which makes the UI show
// a fresh actionable issue for a row the backend says is settled. This list
// MUST cover every member of IssueStatus (types/technicalAudit.ts).
const isIssueStatus = (status?: string): status is PageIssue["fix_status"] =>
  status === "pending" ||
  status === "approved" ||
  status === "rejected" ||
  status === "applied" ||
  status === "recommended" ||
  status === "verified" ||
  status === "failed" ||
  status === "skipped" ||
  status === "protected" ||
  status === "obsolete" ||
  status === "manual" ||
  status === "manual_applied";

const normalizeFixStatus = (status?: string): PageIssue["fix_status"] => {
  if (isIssueStatus(status)) return status;
  return "pending";
};

const isLargeImageResultLike = (record: RawApiObject): boolean => {
  if (
    record.issue_type === "large_image" ||
    record.method === "image_optimize"
  ) {
    return true;
  }
  return [
    "original_size",
    "new_size",
    "bytes_saved",
    "new_dimensions",
    "still_over_max",
    "image_context",
  ].some((key) => Object.prototype.hasOwnProperty.call(record, key));
};

const pickLargeImageResult = (
  ...candidates: unknown[]
): LargeImageProofResult | null => {
  for (const candidate of candidates) {
    const record = parseMaybeObject(candidate);
    if (!record) continue;
    const nestedCandidates = [
      record,
      parseMaybeObject(record.result),
      parseMaybeObject(record.proof),
      parseMaybeObject(record.large_image_result),
      parseMaybeObject(record.largeImageResult),
    ];
    for (const nested of nestedCandidates) {
      if (nested && isLargeImageResultLike(nested)) {
        return nested as LargeImageProofResult;
      }
    }
  }
  return null;
};

const normalizeLargeImageResult = (
  raw: RawApiObject,
): LargeImageProofResult | null => {
  const issueType = String(raw.issue_type ?? raw.issueType ?? "");
  if (issueType !== "large_image") return null;
  const result = pickLargeImageResult(
    raw.large_image_result,
    raw.largeImageResult,
    raw.proof_result,
    raw.proofResult,
    raw.proof,
    raw.apply_result,
    raw.applyResult,
    raw.fix_result,
    raw.fixResult,
    raw.result,
    raw.latest_fix,
    raw.latestFix,
  );
  if (result) return result;

  const rawStatus = asString(raw.fix_status ?? raw.fixStatus);
  if (rawStatus === "skipped" || rawStatus === "protected") {
    return {
      status: rawStatus,
      issue_type: "large_image",
      message: asString(raw.message),
    };
  }

  const error = asString(raw.error ?? raw.apply_error ?? raw.applyError);
  if (error && rawStatus === "failed") {
    return {
      status: "failed",
      issue_type: "large_image",
      error,
      success: false,
    };
  }

  return null;
};

const normalizeIssueDetails = (raw: RawApiObject): RawApiObject => {
  const issueType = String(raw.issue_type ?? raw.issueType ?? "");
  const details = { ...asRecord(raw.issue_details ?? raw.issueDetails) };

  if (issueType === "duplicate_title") {
    if (
      details.title === undefined &&
      typeof details.duplicate_title === "string"
    ) {
      details.title = details.duplicate_title;
    }
    if (
      details.count === undefined &&
      typeof details.duplicate_count === "number"
    ) {
      details.count = details.duplicate_count;
    }
  }

  if (issueType === "duplicate_meta") {
    if (
      details.description === undefined &&
      typeof details.duplicate_meta_description === "string"
    ) {
      details.description = details.duplicate_meta_description;
    }
    if (
      details.count === undefined &&
      typeof details.duplicate_count === "number"
    ) {
      details.count = details.duplicate_count;
    }
  }

  return details;
};

const normalizeIssue = (i: RawApiObject): PageIssue => ({
  ...i,
  id: String(i.id ?? ""),
  run_id: String(i.run_id ?? i.runId ?? ""),
  issue_type: (i.issue_type ?? i.issueType) as PageIssue["issue_type"],
  page_url: String(i.page_url ?? i.pageUrl ?? ""),
  page_title: asString(i.page_title ?? i.pageTitle) ?? null,
  status_code:
    typeof (i.status_code ?? i.statusCode) === "number"
      ? ((i.status_code ?? i.statusCode) as number)
      : null,
  indexability: asString(i.indexability) ?? null,
  issue_details: normalizeIssueDetails(i),
  severity: normalizeSeverity(asString(i.severity)),
  fix_status: normalizeFixStatus(asString(i.fix_status ?? i.fixStatus)),
  original_value: asString(i.original_value ?? i.originalValue) ?? null,
  proposed_value: asString(i.proposed_value ?? i.proposedValue) ?? null,
  proposed_fix: parseMaybeJson(
    i.proposed_fix ?? i.proposedFix ?? i.proposed_value ?? i.proposedValue,
  ),
  approved_value: parseMaybeJson(i.approved_value ?? i.approvedValue),
  error: asString(i.error ?? i.apply_error ?? i.applyError) ?? null,
  created_at: asString(i.created_at ?? i.createdAt) ?? new Date().toISOString(),
  approved_at: asString(i.approved_at ?? i.approvedAt) ?? null,
  applied_at: asString(i.applied_at ?? i.appliedAt) ?? null,
  large_image_result: normalizeLargeImageResult(i),
  apply_result: parseMaybeObject(i.apply_result ?? i.applyResult) ?? null,
  fix_result: parseMaybeObject(i.fix_result ?? i.fixResult) ?? null,
  result: parseMaybeObject(i.result) ?? null,
  content_type: readContentType(i),
  wordpress_id: asNumber(i.wordpress_id ?? i.wordpressId),
  modified_date: asString(i.modified_date ?? i.modifiedDate) ?? null,
  fix_target:
    (asString(i.fix_target ?? i.fixTarget) as PageIssue["fix_target"]) ?? null,
  auto_fix_excluded_reason:
    asString(i.auto_fix_excluded_reason ?? i.autoFixExcludedReason) ?? null,
  generation_status:
    (asString(
      i.generation_status ?? i.generationStatus,
    ) as PageIssue["generation_status"]) ?? null,
});

const normalizeRun = (run: RawApiObject): AuditRunSummary => {
  const rawIssues = Array.isArray(run.issues) ? run.issues : [];
  const issues = rawIssues.map((issue) => normalizeIssue(asRecord(issue)));
  const severityCounts =
    maybeRecord(run.issues_by_severity ?? run.issuesBySeverity) ??
    issues.reduce((acc: Record<string, number>, issue: PageIssue) => {
      acc[issue.severity || "warning"] =
        (acc[issue.severity || "warning"] || 0) + 1;
      return acc;
    }, {});
  const statusCounts =
    maybeRecord(run.issues_by_status ?? run.issuesByStatus) ??
    issues.reduce((acc: Record<string, number>, issue: PageIssue) => {
      acc[issue.fix_status] = (acc[issue.fix_status] || 0) + 1;
      return acc;
    }, {});

  return {
    id: String(run.id ?? ""),
    tenant_id: String(run.tenant_id ?? run.tenantId ?? ""),
    website: String(run.website ?? run.website_url ?? run.websiteUrl ?? ""),
    status: normalizeRunStatus(asString(run.status)),
    total_issues:
      typeof (run.total_issues ?? run.totalIssues) === "number"
        ? ((run.total_issues ?? run.totalIssues) as number)
        : issues.length,
    // Needed to normalize the health score by site size (GH #596). The ORM run
    // carries pages_crawled, so it's already in the raw payload — just map it.
    pages_crawled:
      typeof (run.pages_crawled ?? run.pagesCrawled) === "number"
        ? ((run.pages_crawled ?? run.pagesCrawled) as number)
        : undefined,
    issues_by_severity: normalizeSeveritySummary(severityCounts),
    issues_by_status: normalizeStatusSummary(statusCounts),
    started_at:
      asString(run.started_at ?? run.startedAt ?? run.created_at) ??
      new Date().toISOString(),
    completed_at: asString(run.completed_at ?? run.completedAt) ?? null,
    crawl_config: run.crawl_config ?? run.crawlConfig ?? null,
  };
};

export const technicalAuditService = {
  /**
   * Get issues for a run with filtering, sorting, and pagination
   */
  async getIssues(
    runId: string,
    options?: {
      filters?: IssueFilters;
      sort?: IssueSort;
      page?: number;
      pageSize?: number;
      excludeTypes?: string[];
      includeTypes?: string[];
    },
  ): Promise<IssuesListResponse> {
    const params = new URLSearchParams({
      page: String(options?.page || 1),
      limit: String(options?.pageSize || 20),
    });

    if (options?.filters?.type) params.set("issueType", options.filters.type);
    if (options?.filters?.severity)
      params.set("severity", options.filters.severity);
    if (options?.filters?.status)
      params.set("fixStatus", options.filters.status);
    if (options?.filters?.search) params.set("search", options.filters.search);
    if (options?.excludeTypes?.length)
      params.set("excludeTypes", options.excludeTypes.join(","));
    if (options?.includeTypes?.length)
      params.set("includeTypes", options.includeTypes.join(","));
    if (options?.sort) {
      params.set("sortBy", options.sort.field);
      params.set("sortOrder", options.sort.direction);
    }

    const response = await apiClient.get<RawApiObject>(
      `/technical-audit/runs/${runId}/issues?${params}`,
    );
    const raw = response.data;
    const rawIssues = Array.isArray(raw.issues) ? raw.issues : [];
    const rawPagination = asRecord(raw.pagination);
    const rawSummary = asRecord(raw.summary);

    return {
      issues: rawIssues.map((issue) => normalizeIssue(asRecord(issue))),
      total: typeof rawPagination.total === "number" ? rawPagination.total : 0,
      page: typeof rawPagination.page === "number" ? rawPagination.page : 1,
      page_size:
        typeof rawPagination.limit === "number" ? rawPagination.limit : 20,
      total_pages:
        typeof rawPagination.totalPages === "number"
          ? rawPagination.totalPages
          : 1,
      summary: raw.summary
        ? {
            total: typeof rawSummary.total === "number" ? rawSummary.total : 0,
            by_severity: normalizeSeveritySummary(
              asRecord(rawSummary.bySeverity ?? rawSummary.by_severity),
            ),
            by_status: normalizeStatusSummary(
              asRecord(rawSummary.byStatus ?? rawSummary.by_status),
            ),
            by_type: asRecord(
              rawSummary.byType ?? rawSummary.by_type,
            ) as IssuesListResponse["summary"]["by_type"],
          }
        : emptyIssueSummary(),
    };
  },

  /**
	 * Update single issue status for legacy status corrections or rejection.
   */
  async updateIssue(
    issueId: string,
    data: UpdateIssueRequest,
  ): Promise<PageIssue> {
    const response = await apiClient.patch<PageIssue>(
      `/technical-audit/issues/${issueId}`,
      data,
    );
    return normalizeIssue(response.data);
  },

  /**
   * Bulk update issues status
   */
  async bulkUpdateIssues(data: BulkUpdateRequest): Promise<BulkUpdateResponse> {
    const response = await apiClient.post<BulkUpdateResponse>(
      `/technical-audit/issues/bulk-action`,
      data,
      // A large duplicate-group approve (45+ rows) can exceed the global 30s
      // axios ceiling; the backend batches now (~2-3s typical) but keep ample
      // headroom so a slow DB never surfaces as a spurious "bulk failed".
      { timeout: 180_000 },
    );
    return response.data;
  },

  /**
	 * Apply suggested fixes to WordPress
   */
  async applyFixes(runId: string, dryRun = false): Promise<ApplyFixesResponse> {
    const response = await apiClient.post<ApplyFixesResponse>(
      `/technical-audit/runs/${runId}/apply`,
      { dryRun },
      // Apply is synchronous and does multiple WordPress round-trips per issue;
      // a bulk apply routinely exceeds the global 30s axios timeout and surfaces
      // as a spurious "Network Error". Give apply a much larger ceiling.
      { timeout: 180_000 },
    );
    return response.data;
  },

  /**
   * Consolidated duplicate-review groups for a run (BDD A5): pages sharing the
   * same title / meta description / H1, grouped for side-by-side bulk review.
   */
  async getDuplicateGroups(
    runId: string,
    issueType?: string,
  ): Promise<DuplicateGroupsResponse> {
    const qs = issueType ? `?issueType=${encodeURIComponent(issueType)}` : "";
    const response = await apiClient.get<DuplicateGroupsResponse>(
      `/technical-audit/runs/${runId}/duplicate-groups${qs}`,
    );
    // The endpoint returns members as RAW API rows (proposed_value is a JSON
    // string, proposed_fix is absent). The DuplicateGroup type claims they are
    // fully-decorated PageIssue[] "same shape as the issues list" — but only the
    // issues-list path ran normalizeIssue. So the sibling drawers, which read
    // the parsed `proposed_fix`, rendered an empty "Click Generate fix" field
    // even when a valid distinct suggestion existed on the row. Decorate here so
    // the group members really do match the issues-list shape.
    const data = response.data;
    return {
      ...data,
      groups: (data.groups ?? []).map((g) => ({
        ...g,
        issues: (g.issues ?? []).map((issue) =>
          normalizeIssue(asRecord(issue)),
        ),
      })),
    };
  },

  /**
   * Bulk-generate suggestions for a set of duplicate issues. Queues them for
   * the throttled background drain — poll generation_status for completion.
   */
  async bulkGenerateSuggestions(
    issueIds: string[],
    force = false,
    includeApproved = false,
  ): Promise<BulkGenerateSuggestionsResponse> {
    const response = await apiClient.post<BulkGenerateSuggestionsResponse>(
      `/technical-audit/issues/bulk-generate-suggestions`,
      { issueIds, force, includeApproved },
    );
    return response.data;
  },

  /**
   * "Apply selected" (BDD A5): batch-apply ONLY the given approved issues via
   * the run apply endpoint's issueIds allowlist. Same long timeout as applyFixes
   * — each issue is a WordPress round-trip.
   */
  async applySelectedFixes(
    runId: string,
    issueIds: string[],
  ): Promise<ApplyFixesResponse> {
    const response = await apiClient.post<ApplyFixesResponse>(
      `/technical-audit/runs/${runId}/apply`,
      { issueIds },
      // Big duplicate groups (45+ pages, each a WP write + rendered verify)
      // legitimately run past 3 minutes.
      { timeout: 600_000 },
    );
    return response.data;
  },

  async applySingleFix(
    issueId: string,
    data: {
      wp_api_base?: string;
      wp_auth?: unknown;
      approvedValue?: unknown;
      // Force-override a review-only safeguard (e.g. remove a noindex). The
      // backend re-validates per cause and may still answer manual_required.
      force?: boolean;
      // image_missing_dimensions only: specialist-edited manual dimension
      // override (both together, 1..8192). Omit both to let the backend write
      // its measured values; when present the backend skips measurement and
      // returns dimension_source: "manual_override".
      width?: number;
      height?: number;
      // missing_from_sitemap only: the user explicitly confirmed a SITE-WIDE
      // change (un-excluding a whole post type adds every published post of that
      // type to the sitemap AND makes them indexable). The backend refuses that
      // apply unless this is true, and the UI only ever sets it from the
      // deliberate confirmation checkbox in MissingFromSitemapCard.
      confirm_site_wide?: boolean;
    } = {},
  ): Promise<ApplySingleFixResponse> {
    const response = await apiClient.post(
      `/technical-audit/issues/${issueId}/apply`,
      data,
      // A single apply still does several WordPress round-trips (resolve →
      // update → verify); raise the ceiling above the global 30s to avoid
      // spurious "Network Error" timeouts on slower sites.
      { timeout: 180_000 },
    );
    return response.data as ApplySingleFixResponse;
  },

  async getFixHistory(issueId: string): Promise<FixHistory> {
    const response = await apiClient.get<FixHistory>(
      `/technical-audit/issues/${issueId}/fix-history`,
    );
    return response.data;
  },

  async getIssueHistory(issueId: string): Promise<FixHistoryEntry[]> {
    const response = await apiClient.get<FixHistoryEntry[]>(
      `/technical-audit/issues/${issueId}/history`,
    );
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * Context-aware recovery actions for a FAILED fix apply. The backend
   * classifies the failure cause (plugin_missing / key_mismatch / creds_invalid)
   * and returns the URLs the user needs to recover. Only meaningful when the
   * issue's fix_status === "failed"; kind: null means no actions.
   */
  async getRecoveryActions(issueId: string): Promise<RecoveryActions> {
    const response = await apiClient.get<RecoveryActions>(
      `/technical-audit/issues/${issueId}/recovery-actions`,
    );
    return response.data;
  },

  /**
   * Pre-apply publish-first signal: is this issue's source page publicly live?
   * For source-page content-edit fixes on a page that 404s to the public, the
   * drawer / Review Fix dialog disables Apply and prompts the user to publish
   * the page first. Non-eligible issue types return applicable: false.
   */
  async getSourcePublishState(issueId: string): Promise<SourcePublishState> {
    const response = await apiClient.get<SourcePublishState>(
      `/technical-audit/issues/${issueId}/source-publish-state`,
    );
    return response.data;
  },

  /**
   * Resolve the actions the publish-first chooser can offer for an unpublished
   * source page: the correct WP editor deep link (post id resolved server-side),
   * whether it can be published, and an in-app editor target when the page maps
   * to app content. Does a live WP lookup — fetch on demand, not on every render.
   */
  async getPublishTargets(issueId: string): Promise<PublishTargets> {
    const response = await apiClient.get<PublishTargets>(
      `/technical-audit/issues/${issueId}/publish-targets`,
    );
    return response.data;
  },

  /**
   * Self-healing sweep: re-check a run's still-open issues against the live site
   * and close stale ones (page became a non-editable archive, went unpublished, or
   * the flagged broken link was already removed). Bounded — repeat for large runs.
   */
  async reverifyStale(
    runId: string,
    limit = 50,
  ): Promise<{
    checked: number;
    closed: number;
    kept: number;
    by_reason: Record<string, number>;
  }> {
    const response = await apiClient.post(
      `/technical-audit/runs/${runId}/reverify-stale`,
      { limit },
    );
    return response.data;
  },

  /**
   * File a manual-fix ticket for an issue that can't be safely auto-applied.
   * Marks it 'manual' (out of the actionable queue, not an error) and notifies
   * the AgileCo team so an expert can fix it by hand.
   */
  async requestManualFix(
    issueId: string,
    note?: string,
  ): Promise<{ success: boolean; fix_status: string; message?: string }> {
    const response = await apiClient.post(
      `/technical-audit/issues/${issueId}/request-manual-fix`,
      note ? { note } : {},
    );
    return response.data;
  },

  /**
   * Client opts in to hands-on help from the Agile & Co team for this issue.
   *
   * Files the same ticket the internal board runs on, and records the consent —
   * this is an opt-in to a separate, human-delivered service, NOT an automated
   * fix, so it must only ever be called after the confirm dialog.
   */
  async requestExpertHelp(
    issueId: string,
    note?: string,
  ): Promise<{
    success: boolean;
    fix_status: string;
    expert_help?: boolean;
    consent_version?: string;
    message?: string;
  }> {
    const response = await apiClient.post(
      `/technical-audit/issues/${issueId}/expert-help`,
      note ? { note } : {},
    );
    return response.data;
  },

  /** Cross-workspace recommendation backlog: issues from each member
   *  workspace's latest crawl with no AI recommendation yet
   *  (Site Health > Tickets > Needs recommendation). */
  async getRecommendationBacklog(): Promise<RecommendationBacklogResponse> {
    const response = await apiClient.get<RecommendationBacklogResponse>(
      `/technical-audit/recommendation-backlog/all`,
    );
    return response.data;
  },

  /** Queue backlog issues for background suggestion generation (any
   *  auto-suggest-capable type). Poll generation_status for completion. */
  async queueRecommendationBacklog(
    issueIds: string[],
  ): Promise<{ queued: number; auto_suggest_queued_issue_ids: string[] }> {
    const response = await apiClient.post(
      `/technical-audit/recommendation-backlog/queue`,
      { issueIds },
    );
    return response.data;
  },

  /** Cross-workspace tickets: every manual-fix ticket in every workspace the
   *  user is a member of (Site Health > Tickets page). */
  async getAllTickets(): Promise<AllTicketsResponse> {
    const response = await apiClient.get<AllTicketsResponse>(
      `/technical-audit/tickets/all`,
    );
    return response.data;
  },

  /** All manual-fix tickets for a tenant, across every crawl (open + resolved). */
  async getTickets(tenantId: string): Promise<TicketsResponse> {
    const response = await apiClient.get<TicketsResponse>(
      `/technical-audit/tickets?tenant_id=${encodeURIComponent(tenantId)}`,
    );
    return response.data;
  },

  /** Auto-resolve tickets whose flagged link is already gone on a live re-check. */
  async reconcileTickets(
    tenantId: string,
  ): Promise<{ checked: number; resolved: number }> {
    const response = await apiClient.post(
      `/technical-audit/tickets/reconcile?tenant_id=${encodeURIComponent(tenantId)}`,
      {},
    );
    return response.data;
  },

  /** Mark a manual-fix ticket resolved (an expert fixed it by hand). */
  async resolveTicket(
    issueId: string,
    note?: string,
  ): Promise<{ success: boolean; resolved: boolean }> {
    const response = await apiClient.post(
      `/technical-audit/issues/${issueId}/resolve-ticket`,
      note ? { note } : {},
    );
    return response.data;
  },

  /** The expert roster for the ticket assign dropdown. */
  async getExperts(): Promise<{ experts: { id: string; name: string }[] }> {
    const response = await apiClient.get(`/technical-audit/experts`);
    return response.data;
  },

  /** Reassign a ticket to a different expert. */
  async setTicketAssignee(
    issueId: string,
    expertId: string,
  ): Promise<{ success: boolean; assignee: string | null; assignee_name: string | null }> {
    const response = await apiClient.post(
      `/technical-audit/issues/${issueId}/ticket-assignee`,
      { expert_id: expertId || null },
    );
    return response.data;
  },

  /** Move a ticket across the workflow (open / in_progress / resolved). */
  async setTicketState(
    issueId: string,
    state: "open" | "in_progress" | "resolved",
  ): Promise<{ success: boolean; state: string }> {
    const response = await apiClient.post(
      `/technical-audit/issues/${issueId}/ticket-state`,
      { state },
    );
    return response.data;
  },

  /** Publish the issue's unpublished source page in WordPress (draft -> publish). */
  async publishSource(issueId: string): Promise<PublishSourceResult> {
    const response = await apiClient.post<PublishSourceResult>(
      `/technical-audit/issues/${issueId}/publish-source`,
      {},
    );
    return response.data;
  },

  // Preview-only: generate a candidate replacement image (fal.ai). No WP write.
  async regenerateImage(
    issueId: string,
    prompt?: string,
  ): Promise<{ candidate_image_url: string; prompt: string }> {
    const response = await apiClient.post<{
      candidate_image_url: string;
      prompt: string;
    }>(
      `/technical-audit/issues/${issueId}/regenerate-image`,
      prompt ? { prompt } : {},
      { timeout: 120000 }, // image generation can exceed the global 30s timeout
    );
    return response.data;
  },

  async revertFix(
    issueId: string,
    data: { applied_by?: string; wp_api_base?: string; wp_auth?: unknown } = {},
  ): Promise<{ success: boolean; fix_status?: string; error?: string }> {
    const response = await apiClient.post(
      `/technical-audit/issues/${issueId}/revert`,
      data,
      { timeout: 180_000 },
    );
    return response.data as {
      success: boolean;
      fix_status?: string;
      error?: string;
    };
  },

  async previewFix(issueId: string): Promise<TechnicalAuditFixPreview> {
    const response = await apiClient.post<TechnicalAuditFixPreview>(
      `/technical-audit/issues/${issueId}/preview`,
      {},
    );
    return response.data;
  },

  async generateSuggestion(issueId: string, force = false): Promise<PageIssue> {
    const response = await apiClient.post(
      `/technical-audit/issues/${issueId}/generate-suggestion`,
      { force },
      { timeout: 180_000 },
    );
    return normalizeIssue(response.data as RawApiObject);
  },

  async verifyFix(issueId: string): Promise<{
    success: boolean;
    fix_status?: string;
    reason?: string;
    result_status?: string;
    requires_manual_verification?: boolean;
    verification_mode?: string;
    evidence?: Record<string, unknown>;
  }> {
    const response = await apiClient.post(
      `/technical-audit/issues/${issueId}/verify`,
      {},
    );
    return response.data as {
      success: boolean;
      fix_status?: string;
      reason?: string;
      result_status?: string;
      requires_manual_verification?: boolean;
      verification_mode?: string;
      evidence?: Record<string, unknown>;
    };
  },

  /**
   * Existing on-page headings the operator can promote to H1, instead of
   * accepting generated wording. Read-only; never applies anything.
   */
  async getH1PromoteCandidates(issueId: string): Promise<H1PromoteCandidates> {
    const response = await apiClient.get(
      `/technical-audit/issues/${issueId}/h1-candidates`,
      { timeout: 30_000 },
    );
    return response.data as H1PromoteCandidates;
  },

  /**
   * Review-only: candidate replacement URLs for a dead external link
   * (Wayback + web search). The reviewer picks one to repoint — never applied
   * automatically. Returns enabled=false when the backend flag is off.
   */
  async suggestBrokenExternalReplacements(issueId: string): Promise<{
    enabled: boolean;
    dead_url?: string | null;
    anchor_text?: string;
    original_reachable?: boolean;
    candidates: { url: string; source: "wayback" | "search"; title: string }[];
  }> {
    const response = await apiClient.get(
      `/technical-audit/issues/${issueId}/broken-external-candidates`,
      { timeout: 20_000 },
    );
    return response.data as {
      enabled: boolean;
      dead_url?: string | null;
      anchor_text?: string;
      candidates: { url: string; source: "wayback" | "search"; title: string }[];
    };
  },

  /**
   * LLM-rewrite the paragraph/section that contains a dead external link so the
   * copy reads naturally without it. Review-only — returns before/after HTML for
   * the reviewer to approve; never auto-applies. enabled=false when the flag is off.
   */
  async rewriteBrokenExternalSection(
    issueId: string,
    scope: "remove_link" | "sentence" | "paragraph" | "section" = "sentence",
  ): Promise<{
    enabled: boolean;
    scope?: string;
    before_html?: string;
    after_html?: string;
    error?: string;
  }> {
    const response = await apiClient.post(
      `/technical-audit/issues/${issueId}/rewrite-broken-external-section`,
      {},
      { params: { scope }, timeout: 60_000 },
    );
    return response.data as {
      enabled: boolean;
      scope?: string;
      before_html?: string;
      after_html?: string;
      error?: string;
    };
  },

  /**
   * List all audit runs for a tenant
   */
  async listRuns(
    tenantId: string,
    page = 1,
    limit = 20,
  ): Promise<AuditRunsListResponse> {
    const params = new URLSearchParams({
      tenant_id: tenantId,
      page: String(page),
      limit: String(limit),
    });

    const response = await apiClient.get<
      AuditRunsListResponse | AuditRunSummary[]
    >(`/technical-audit/runs?${params}`);
    const raw = response.data;
    if (Array.isArray(raw)) {
      return { runs: raw.map(normalizeRun), total: raw.length };
    }
    return {
      runs: (raw.runs || []).map(normalizeRun),
      total: raw.total ?? raw.runs?.length ?? 0,
    };
  },

  /**
   * Get a single audit run by ID
   */
  async getRun(runId: string): Promise<AuditRunSummary> {
    const response = await apiClient.get<AuditRunSummary>(
      `/technical-audit/runs/${runId}`,
    );
    return normalizeRun(response.data);
  },

  /**
   * Start a new crawl
   */
  async startCrawl(config: CrawlConfig): Promise<StartCrawlResponse> {
    const response = await apiClient.post<StartCrawlResponse>(
      "/technical-audit/crawl/start",
      {
        tenant_id: config.tenant_id,
        website: config.website,
        config,
      },
    );
    return response.data;
  },

  /**
   * Enhance audit run with DataForSEO on-page data
   */
  async dfsEnhance(
    runId: string,
  ): Promise<{
    dfs_issues_added: number;
    duplicates_skipped: number;
    onpage_score: number;
  }> {
    const response = await apiClient.post<{
      dfs_issues_added: number;
      duplicates_skipped: number;
      onpage_score: number;
    }>(`/technical-audit/runs/${runId}/dfs-enhance`);
    return response.data;
  },

  /**
   * Get crawl status
   */
  async getCrawlStatus(crawlId: string): Promise<CrawlStatus> {
    const response = await apiClient.get<CrawlStatus>(
      `/technical-audit/crawl/${crawlId}/status`,
    );
    return response.data;
  },
};
